import { Modal, Form, Input, Switch, Row, Col } from 'antd';
import { useEffect } from 'react';

const ClinicForm = ({ open, onClose, onSubmit, initialValues }) => {
  const [form] = Form.useForm();
  const isEdit = !!initialValues;
  const onlyDigits = (maxLength) => (e) => e.target.value.replace(/\D/g, '').slice(0, maxLength);

  useEffect(() => {
    if (open) {
      if (initialValues) {
        form.setFieldsValue({
          name:     initialValues.name,
          address:  initialValues.address,
          city:     initialValues.city,
          state:    initialValues.state,
          pincode:  initialValues.pincode,
          mobile:   initialValues.mobile,
          landline: initialValues.landline,
          email:    initialValues.email,
          isActive: initialValues.isActive,
        });
      } else {
        form.resetFields();
      }
    }
  }, [open, initialValues, form]);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    await onSubmit(values);
  };

  // Clinic name: letters, spaces and & only, max 100 chars
  // const clinicNameRule = {
  //   pattern: /^(?!\s)(?!.*\s{2,})(?=.*[A-Za-z])[A-Za-z0-9\s&]+(?<!\s)$/,
  //   message: 'Must contain at least one letter. Only letters, numbers, spaces and & allowed',
  // };
    const clinicNameRule = {
      pattern: /^(?!\s)(?!.*\s{2,})(?=.*[A-Za-z])[A-Za-z0-9\s&/]+$/,
      message:
        'Must contain at least one letter. Only letters, numbers, spaces, &, and / are allowed',
    };
  // City / State: letters and spaces only
  const textOnlyRule = {
    pattern: /^(?!\s)(?!.*\s{2,})[A-Za-z\s]+(?<!\s)$/,
    message: 'Only letters allowed. No leading, trailing, or multiple spaces',
  };

  const emailRule = { type: 'email', message: 'Enter a valid email address' };

  const mobileRule = {
    pattern: /^[1-9][0-9]{9}$/,
    message: 'Mobile number must be exactly 10 digits and must not start with 0',
  };

  const landlineRule = {
    pattern: /^0[1-9][0-9]{9}$/,
    message: 'Landline must be 11 digits starting with 0 (e.g. 04412345678)',
  };

  // Pincode validation
  const pincodeRule = {
    pattern: /^[0-9]{6}$/,
    message: 'Pincode must be exactly 6 digits',
  };

  return (
    <Modal
      title={isEdit ? 'Edit Clinic' : 'Add New Clinic'}
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      width={640}
      destroyOnClose
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }} autoComplete="off">
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="name"
              label="Clinic Name"
              rules={[
                { required: true, message: 'Clinic name is required' },
                { max: 65, message: 'Clinic name cannot exceed 65 characters' },
                clinicNameRule,
              ]}
            >
              <Input placeholder="Enter clinic name" maxLength={65} showCount />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              name="address"
              label="Address"
              rules={[
                { required: true, message: 'Address is required' },
                { min: 15, message: 'Address must be at least 15 characters' },
                { max: 200, message: 'Address cannot exceed 200 characters' },
              ]}
            >
              <Input.TextArea rows={2} placeholder="Enter address" maxLength={200} showCount />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="state"
              label="State"
              rules={[
                { required: true, message: 'State is required' },
                textOnlyRule,
              ]}
            >
              <Input placeholder="Enter state" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="city"
              label="City"
              rules={[
                { required: true, message: 'City is required' },
                textOnlyRule,
              ]}
            >
              <Input placeholder="Enter city" />
            </Form.Item>
          </Col>

          <Col span={12}>
          <Form.Item
            name="pincode"
            label="Pincode"
            getValueFromEvent={onlyDigits(6)}
            rules={[
              { required: true, message: 'Pincode is required' },
              pincodeRule,
            ]}
          >
            <Input
              placeholder="Enter 6-digit pincode"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
            />
          </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="mobile"
              label="Mobile Number"
              getValueFromEvent={onlyDigits(10)}
              rules={[mobileRule]}
            >
              <Input
                placeholder="Enter 10-digit mobile number"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={10}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="landline"
              label="Landline Number"
              getValueFromEvent={onlyDigits(11)}
              rules={[landlineRule]}
            >
              <Input
                placeholder="e.g. 04412345678"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={11}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="email"
              label="Email Address"
              rules={[emailRule]}
            >
              <Input placeholder="Enter email address" />
            </Form.Item>
          </Col>

          {isEdit && (
            <Col span={12}>
              <Form.Item
                name="isActive"
                label="Active"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          )}
        </Row>
      </Form>
    </Modal>
  );
};

export default ClinicForm;
